'use client';

import { Alert, AlertDescription } from '@repo/ui/components/ui/alert';
import { Badge } from '@repo/ui/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { AlertTriangle } from 'lucide-react';
import { InventaireResponse } from '@repo/types';
import { StepNavigation } from '../../../shared/components/step-navigation';
import { DetailsFormField } from '../../../shared/components/details-form-field';
import { IdentificationDetails } from '../../../shared/components/identification-details';
import { EssenceIllustrationPage } from '../../../shared/components/essence-illustration-page';
import { toEssenceIllustrationModel } from '../../../shared/components/essence-illustration-model';
import { FormData } from '../../../shared/types';
import { getDynamicFieldOptions } from '../../edit/utils/dynamic-field-options';
import { BevaDetails } from './beva-details';
import { HistoryAccordion } from './history-accordion';
import { TravauxDetails } from './travaux-details';
import { Step } from '../../../shared/types';
import { RisqueDeRuptureDetails } from './rsique-rupture-table';

interface DetailsArbreContentProps {
  currentStep: number;
  currentStepData: Step;
  steps: Step[];
  formData: FormData;
  originalData: InventaireResponse | null;
  completedSteps: Set<number>;
  viewMode: 'horizontal' | 'vertical';
  onStepClick: (step: number) => void;
  onViewModeChange: (mode: 'horizontal' | 'vertical') => void;
  champignons: any[];
  especes: any[];
  nuisibles: any[];
  isLoadingInventoryData: boolean;
  hasInventoryDataError: any;
  champignonsError?: string | null;
  especesError?: string | null;
  nuisiblesError?: string | null;
  inventaireId: number;
}

export function DetailsArbreContent({
  currentStep,
  currentStepData,
  steps,
  formData,
  originalData,
  completedSteps,
  viewMode,
  onStepClick,
  onViewModeChange,
  champignons,
  especes,
  nuisibles,
  isLoadingInventoryData,
  hasInventoryDataError,
  champignonsError,
  especesError,
  nuisiblesError,
  inventaireId,
}: DetailsArbreContentProps) {
  function renderStepContent() {
    if (currentStepData.id === 'identification') {
      return <IdentificationDetails formData={formData} originalData={originalData} />;
    }
    if (currentStepData.id === 'illustrations-essence') {
      const espece =
        originalData?.data?.arbre?.espece ??
        (formData['_espece'] as { name?: string; nom_fr?: string; img_url?: string } | undefined);
      const essenceModels = espece ? [toEssenceIllustrationModel(espece)] : [];
      return <EssenceIllustrationPage essences={essenceModels} />;
    }
    if (currentStepData.id === 'historique') {
      return <HistoryAccordion inventaireId={inventaireId} />;
    }
    if (currentStepData.id === 'preconisation-travaux') {
      return (
        <TravauxDetails
          formData={formData}
          originalData={originalData}
          arbreId={originalData?.data?.arbre?.id}
          steps={steps}
        />
      );
    }
    if (currentStepData.id === 'estimation-valeur') {
      return <BevaDetails formData={formData} originalData={originalData} inventaireId={inventaireId} />;
    }

    return (
      <div className="space-y-4">
        {currentStepData.fields.map((field) => {
          const dynamicOptions = getDynamicFieldOptions(field, champignons, especes, nuisibles);
          return (
            <DetailsFormField
              key={field.name}
              field={field}
              value={formData[field.name] || ''}
              dynamicOptions={dynamicOptions}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className={`flex flex-1 gap-4 overflow-hidden ${viewMode === 'vertical' ? 'flex-row' : 'flex-col'}`}>
      {viewMode === 'vertical' && (
        <aside className="w-72 shrink-0" aria-label="Navigation des étapes">
          <StepNavigation
            steps={steps}
            currentStep={currentStep}
            completedSteps={completedSteps}
            onStepClick={onStepClick}
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
          />
        </aside>
      )}

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {viewMode === 'horizontal' && (
          <nav className="mb-3" aria-label="Navigation des étapes">
            <StepNavigation
              steps={steps}
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={onStepClick}
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
            />
          </nav>
        )}

        <Card className="mb-3 border-border/60 bg-card shadow-sm ring-1 ring-border/20 dark:border-border/80 dark:ring-border/30">
          <CardHeader className="space-y-2 py-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex-1 space-y-0.5">
                <CardTitle className="flex items-center gap-2.5 text-lg font-semibold leading-tight tracking-tight text-foreground sm:text-xl">
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 text-sm font-semibold text-primary ring-2 ring-primary/20 dark:from-primary/30 dark:to-primary/20 dark:ring-primary/30"
                    aria-label={`Étape ${currentStep + 1}`}
                  >
                    {currentStep + 1}
                  </span>
                  <span>{currentStepData.title}</span>
                </CardTitle>
                <CardDescription className="text-sm font-medium leading-relaxed text-muted-foreground/90 dark:text-muted-foreground/80">
                  {currentStepData.description}
                </CardDescription>
              </div>
              {completedSteps.has(currentStep) && (
                <Badge
                  variant="default"
                  className="h-8 bg-primary px-3 text-xs font-semibold text-primary-foreground shadow-sm hover:bg-primary dark:bg-primary dark:hover:bg-primary"
                >
                  <span className="sr-only">Étape terminée</span>
                  ✓ Terminé
                </Badge>
              )}
            </div>
          </CardHeader>
        </Card>

        {isLoadingInventoryData && (
          <Alert
            className="mb-3 border-primary/20 bg-primary/5 text-foreground ring-1 ring-primary/10 dark:border-primary/30 dark:bg-primary/10 dark:ring-primary/20"
            role="status"
          >
            <AlertTriangle className="h-5 w-5 text-primary" aria-hidden="true" />
            <AlertDescription className="text-sm font-medium">
              Chargement des données d'inventaire (champignons, espèces, nuisibles)...
            </AlertDescription>
          </Alert>
        )}

        {hasInventoryDataError && (
          <Alert
            className="mb-3 border-destructive/50 bg-destructive/10 text-destructive ring-1 ring-destructive/20 dark:border-destructive/50 dark:bg-destructive/10 dark:ring-destructive/30"
            role="alert"
          >
            <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />
            <AlertDescription className="text-sm font-medium">
              Erreur lors du chargement des données d'inventaire:
              {champignonsError && ` Champignons: ${champignonsError}`}
              {especesError && ` Espèces: ${especesError}`}
              {nuisiblesError && ` Nuisibles: ${nuisiblesError}`}
            </AlertDescription>
          </Alert>
        )}

        <Card className="flex-1 overflow-y-auto border-border/60 bg-card shadow-sm ring-1 ring-border/20 dark:border-border/80 dark:ring-border/30">
          <CardContent className="p-4 sm:p-5">{renderStepContent()}</CardContent>
        </Card>
      </div>
    </div>
  );
}

